// Curated top-of-line trade samples per discipline.
// All swatches are bespoke high-resolution images generated for the studio,
// served locally from /public/products/ so they never fail to load.
//
// Each product carries two extra pieces of metadata used on the /collection
// page to broaden how clients browse:
//
//   • `moods`   — colour-family tags used by the mood filter row
//                 (Warm · Cool · Neutral · Dark · Light · Natural)
//   • `colours` — the tonal palette this finish is offered in; rendered as
//                 a hairline chip strip on each card. Hex values are the
//                 studio's approved reference tones. First colour uses the
//                 base swatch image; each subsequent colour renders from
//                 /products/<id>-<colour-slug>.jpg.
export const PRODUCTS = [
  // ── TIMBER — the wide-plank oak canon ─────────────────────────
  {
    id: "tmb-luxplank",
    name: "LUXplank Engineered",
    discipline: "Timber",
    finish: "Premium engineered wide-plank · Matte low-sheen · 10 curated tones",
    swatch: "/products/luxplank/1540-scandi.jpg",
    tag: "Signature Range · Engineered",
    moods: ["Warm", "Cool", "Neutral", "Dark", "Light", "Natural"],
    colours: [
      { name: "1540 Scandi",     hex: "#E5D5B8", image: "/products/luxplank/1540-scandi.jpg" },
      { name: "1541 Hampton",    hex: "#E5CFAB", image: "/products/luxplank/1541-hampton.jpg" },
      { name: "1542 Modern",     hex: "#DFCFB0", image: "/products/luxplank/1542-modern.jpg" },
      { name: "1543 Coastal",    hex: "#E6C88F", image: "/products/luxplank/1543-coastal.jpg" },
      { name: "1544 Boho",       hex: "#C7A374", image: "/products/luxplank/1544-boho.jpg" },
      { name: "1545 Farmhouse",  hex: "#B08758", image: "/products/luxplank/1545-farmhouse.jpg" },
      { name: "1546 Vintage",    hex: "#7D5A3C", image: "/products/luxplank/1546-vintage.jpg" },
      { name: "1547 Provincial", hex: "#573B24", image: "/products/luxplank/1547-provincial.jpg" },
      { name: "1548 Heritage",   hex: "#3A281A", image: "/products/luxplank/1548-heritage.jpg" },
      { name: "1549 Mono",       hex: "#2D2C2A", image: "/products/luxplank/1549-mono.jpg" },
    ],
  },
  {
    id: "tmb-versailles-parquet",
    name: "Versailles Custom Parquet",
    discipline: "Timber",
    finish: "Oak · Wire-brushed matte · Bespoke geometry",
    swatch: "/products/timber-versailles-parquet.jpg",
    tag: "Bespoke · Statement",
    moods: ["Warm", "Neutral", "Natural"],
    colours: [
      { name: "Honey", hex: "#c8a06a", image: "/products/timber-versailles-parquet.jpg" },
      { name: "Champagne", hex: "#d9c093", image: "/products/tmb-versailles-parquet-champagne.jpg" },
      { name: "Fumed", hex: "#7a5732", image: "/products/tmb-versailles-parquet-fumed.jpg" },
      { name: "Smoked", hex: "#4a341f", image: "/products/tmb-versailles-parquet-smoked.jpg" },
      { name: "Rustic", hex: "#a06940", image: "/products/tmb-versailles-parquet-rustic.jpg" },
    ],
  },
  {
    id: "tmb-euro-oak-natural",
    name: "Oak · Natural",
    discipline: "Timber",
    finish: "Wire-brushed · Matte oil",
    swatch: "/products/timber-euro-oak-natural.jpg",
    tag: "Wide-plank · Blonde",
    moods: ["Warm", "Light", "Natural"],
    colours: [
      { name: "Blonde", hex: "#d8b986", image: "/products/timber-euro-oak-natural.jpg" },
      { name: "Wheat", hex: "#c9a878", image: "/products/tmb-euro-oak-natural-wheat.jpg" },
      { name: "Ash", hex: "#c2b299", image: "/products/tmb-euro-oak-natural-ash.jpg" },
      { name: "Whitewash", hex: "#e5dcc9", image: "/products/tmb-euro-oak-natural-whitewash.jpg" },
      { name: "Bleached", hex: "#eee4d3", image: "/products/tmb-euro-oak-natural-bleached.jpg" },
    ],
  },
  {
    id: "tmb-euro-oak-smoked",
    name: "Oak · Smoked",
    discipline: "Timber",
    finish: "Fumed · Hardwax oil",
    swatch: "/products/timber-euro-oak-smoked.jpg",
    tag: "Wide-plank · Coffee",
    moods: ["Dark", "Warm", "Natural"],
    colours: [
      { name: "Coffee", hex: "#5b3f26", image: "/products/timber-euro-oak-smoked.jpg" },
      { name: "Umber", hex: "#7b5638", image: "/products/tmb-euro-oak-smoked-umber.jpg" },
      { name: "Espresso", hex: "#3e2a1a", image: "/products/tmb-euro-oak-smoked-espresso.jpg" },
      { name: "Cocoa", hex: "#4a3222", image: "/products/tmb-euro-oak-smoked-cocoa.jpg" },
      { name: "Charred", hex: "#2c1f14", image: "/products/tmb-euro-oak-smoked-charred.jpg" },
    ],
  },
  {
    id: "tmb-walnut-herringbone",
    name: "American Walnut · Herringbone",
    discipline: "Timber",
    finish: "Natural oil · Reddish walnut",
    swatch: "/products/timber-walnut-herringbone.jpg",
    tag: "Parquet · Chocolate",
    moods: ["Dark", "Warm", "Natural"],
    colours: [
      { name: "Natural", hex: "#6d4327", image: "/products/timber-walnut-herringbone.jpg" },
      { name: "Rustic", hex: "#875634", image: "/products/tmb-walnut-herringbone-rustic.jpg" },
      { name: "Chestnut", hex: "#7a4d2b", image: "/products/tmb-walnut-herringbone-chestnut.jpg" },
      { name: "Dark", hex: "#4d2f1b", image: "/products/tmb-walnut-herringbone-dark.jpg" },
      { name: "Charred", hex: "#2c1c10", image: "/products/tmb-walnut-herringbone-charred.jpg" },
    ],
  },
  {
    id: "tmb-oak-chevron",
    name: "Oak · Chevron",
    discipline: "Timber",
    finish: "Wire-brushed · Matte oil",
    swatch: "/products/timber-oak-chevron.jpg",
    tag: "Parquet · Honey",
    moods: ["Warm", "Neutral", "Natural"],
    colours: [
      { name: "Honey", hex: "#c99b62", image: "/products/timber-oak-chevron.jpg" },
      { name: "Champagne", hex: "#d9c093", image: "/products/tmb-oak-chevron-champagne.jpg" },
      { name: "Rustic", hex: "#a06940", image: "/products/tmb-oak-chevron-rustic.jpg" },
      { name: "Cognac", hex: "#a06b3d", image: "/products/tmb-oak-chevron-cognac.jpg" },
      { name: "Charred", hex: "#3f2d1e", image: "/products/tmb-oak-chevron-charred.jpg" },
    ],
  },
  {
    id: "tmb-blackbutt",
    name: "Australian Blackbutt",
    discipline: "Timber",
    finish: "Satin lacquer · Golden blonde",
    swatch: "/products/timber-blackbutt.jpg",
    tag: "Wide-plank · Native",
    moods: ["Warm", "Light", "Natural"],
    colours: [
      { name: "Golden", hex: "#dab473", image: "/products/timber-blackbutt.jpg" },
      { name: "Ivory", hex: "#e8d5b0", image: "/products/tmb-blackbutt-ivory.jpg" },
      { name: "Pale", hex: "#e7d3ad", image: "/products/tmb-blackbutt-pale.jpg" },
      { name: "Amber", hex: "#b98649", image: "/products/tmb-blackbutt-amber.jpg" },
      { name: "Copper", hex: "#a06842", image: "/products/tmb-blackbutt-copper.jpg" },
    ],
  },
  // ── CARPET — wool-first, quiet-luxury palette ─────────────────
  {
    id: "cpt-bremworth-galet-skarn",
    name: "Galet · Chunky Loop",
    discipline: "Carpet",
    finish: "100% Felted NZ wool · Chunky pebble loop",
    swatch: "/products/carpet-bremworth-galet-skarn.jpg",
    tag: "Signature · Loop",
    moods: ["Warm", "Neutral", "Natural"],
    colours: [
      { name: "Skarn", hex: "#948779", image: "/products/carpet-bremworth-galet-skarn.jpg" },
      { name: "Perlato", hex: "#d4c5a6", image: "/products/cpt-bremworth-galet-skarn-perlato.jpg" },
      { name: "Pumice", hex: "#b8ac9c", image: "/products/cpt-bremworth-galet-skarn-pumice.jpg" },
      { name: "Chert", hex: "#55524a", image: "/products/cpt-bremworth-galet-skarn-chert.jpg" },
      { name: "Sage", hex: "#98a08a", image: "/products/cpt-bremworth-galet-skarn-sage.jpg" },
      { name: "Smoky Quartz", hex: "#7a6a5e", image: "/products/cpt-bremworth-galet-skarn-smoky-quartz.jpg" },
    ],
  },
  {
    id: "cpt-highland-loop",
    name: "Highland Wool Loop",
    discipline: "Carpet",
    finish: "100% New Zealand wool",
    swatch: "/products/carpet-highland-wool-loop.jpg",
    tag: "Loop pile",
    moods: ["Neutral", "Light"],
    colours: [
      { name: "Cloud", hex: "#dcd4c5", image: "/products/carpet-highland-wool-loop.jpg" },
      { name: "Oat", hex: "#d0c4ae", image: "/products/cpt-highland-loop-oat.jpg" },
      { name: "Greige", hex: "#b3a693", image: "/products/cpt-highland-loop-greige.jpg" },
      { name: "Slate", hex: "#7a7a76", image: "/products/cpt-highland-loop-slate.jpg" },
      { name: "Charcoal", hex: "#3d3c3a", image: "/products/cpt-highland-loop-charcoal.jpg" },
    ],
  },
  {
    id: "cpt-sisal-hybrid",
    name: "Hybrid Wool Sisal",
    discipline: "Carpet",
    finish: "80% Wool · 20% Sisal",
    swatch: "/products/carpet-sisal-hybrid.jpg",
    tag: "Textural",
    moods: ["Natural", "Warm", "Neutral"],
    colours: [
      { name: "Oatmeal", hex: "#cdbf9f", image: "/products/carpet-sisal-hybrid.jpg" },
      { name: "Bone", hex: "#dccdae", image: "/products/cpt-sisal-hybrid-bone.jpg" },
      { name: "Wheat", hex: "#cbb47f", image: "/products/cpt-sisal-hybrid-wheat.jpg" },
      { name: "Sand", hex: "#c1a875", image: "/products/cpt-sisal-hybrid-sand.jpg" },
      { name: "Flax", hex: "#a89273", image: "/products/cpt-sisal-hybrid-flax.jpg" },
    ],
  },
  {
    id: "cpt-cashmere-sublime",
    name: "Cashmere Touch · Sublime",
    discipline: "Carpet",
    finish: "Nysoft fibre · Silken cut plush",
    swatch: "/products/carpet-cashmere-sublime.jpg",
    tag: "Signature · Plush",
    moods: ["Neutral", "Warm", "Light"],
    colours: [
      { name: "Serene", hex: "#a8a49a", image: "/products/carpet-cashmere-sublime.jpg" },
      { name: "Placid", hex: "#d8ceb8", image: "/products/cpt-cashmere-sublime-placid.jpg" },
      { name: "Elation", hex: "#cdbfa8", image: "/products/cpt-cashmere-sublime-elation.jpg" },
      { name: "Argent", hex: "#b8ada0", image: "/products/cpt-cashmere-sublime-argent.jpg" },
      { name: "Lucent", hex: "#9d8f7d", image: "/products/cpt-cashmere-sublime-lucent.jpg" },
    ],
  },
  {
    id: "cpt-berber-loop",
    name: "Berber Loop",
    discipline: "Carpet",
    finish: "Wool berber · fleck weave",
    swatch: "/products/carpet-berber-loop.jpg",
    tag: "Loop · Textured",
    moods: ["Neutral", "Light", "Natural"],
    colours: [
      { name: "Cream", hex: "#e6dcc6", image: "/products/carpet-berber-loop.jpg" },
      { name: "Latte", hex: "#c2ac8c", image: "/products/cpt-berber-loop-latte.jpg" },
      { name: "Fleck", hex: "#c5b9a3", image: "/products/cpt-berber-loop-fleck.jpg" },
      { name: "Sand", hex: "#b09980", image: "/products/cpt-berber-loop-sand.jpg" },
      { name: "Stone", hex: "#9b9080", image: "/products/cpt-berber-loop-stone.jpg" },
    ],
  },
  {
    id: "cpt-sisal-natural",
    name: "Pure Natural Sisal",
    discipline: "Carpet",
    finish: "100% Sisal",
    swatch: "/products/carpet-sisal-natural.jpg",
    tag: "Natural fibre",
    moods: ["Natural", "Warm"],
    colours: [
      { name: "Straw", hex: "#d3ad64", image: "/products/carpet-sisal-natural.jpg" },
      { name: "Wheat", hex: "#d8b57a", image: "/products/cpt-sisal-natural-wheat.jpg" },
      { name: "Golden", hex: "#b8873e", image: "/products/cpt-sisal-natural-golden.jpg" },
      { name: "Rust", hex: "#8f5a2a", image: "/products/cpt-sisal-natural-rust.jpg" },
      { name: "Sable", hex: "#7a4d24", image: "/products/cpt-sisal-natural-sable.jpg" },
    ],
  },
  // ── TILE — porcelain, terrazzo, and Zellige ───────────────────
  {
    id: "tle-botticino",
    name: "Botticino White",
    discipline: "Tile",
    finish: "Matte porcelain · Soft veining",
    swatch: "/products/tile-botticino.jpg",
    tag: "Signature · Matte",
    moods: ["Light", "Neutral", "Warm"],
    colours: [
      { name: "Warm White", hex: "#eee6d7", image: "/products/tile-botticino.jpg" },
      { name: "Chalk", hex: "#efe8d8", image: "/products/tle-botticino-chalk.jpg" },
      { name: "Ivory", hex: "#e8dbc4", image: "/products/tle-botticino-ivory.jpg" },
      { name: "Cream", hex: "#dcd0ba", image: "/products/tle-botticino-cream.jpg" },
      { name: "Bone", hex: "#c8bda9", image: "/products/tle-botticino-bone.jpg" },
    ],
  },
  {
    id: "tle-calacatta-slab",
    name: "Calacatta Porcelain Slab",
    discipline: "Tile",
    finish: "Polished · 1200 × 2400",
    swatch: "/products/tile-calacatta-porcelain.jpg",
    tag: "Large-format",
    moods: ["Light", "Cool", "Dark"],
    colours: [
      { name: "Statuario", hex: "#eaeae2", image: "/products/tile-calacatta-porcelain.jpg" },
      { name: "Calacatta", hex: "#e0dccf", image: "/products/tle-calacatta-slab-calacatta.jpg" },
      { name: "Verde", hex: "#4a5d54", image: "/products/tle-calacatta-slab-verde.jpg" },
      { name: "Nero", hex: "#2a2a2a", image: "/products/tle-calacatta-slab-nero.jpg" },
      { name: "Grey", hex: "#8e9299", image: "/products/tle-calacatta-slab-grey.jpg" },
    ],
  },
  {
    id: "tle-terrazzo",
    name: "Custom Terrazzo",
    discipline: "Tile",
    finish: "Honed · Bespoke aggregate",
    swatch: "/products/tile-terrazzo-custom.jpg",
    tag: "Poured · Cream",
    moods: ["Neutral", "Natural", "Warm"],
    colours: [
      { name: "Cream", hex: "#d9cdb3", image: "/products/tile-terrazzo-custom.jpg" },
      { name: "Blush", hex: "#d4b5a8", image: "/products/tle-terrazzo-blush.jpg" },
      { name: "Rose", hex: "#c99f88", image: "/products/tle-terrazzo-rose.jpg" },
      { name: "Stone", hex: "#a89985", image: "/products/tle-terrazzo-stone.jpg" },
      { name: "Charcoal", hex: "#3d3936", image: "/products/tle-terrazzo-charcoal.jpg" },
    ],
  },
  {
    id: "tle-zellige",
    name: "Moroccan Zellige",
    discipline: "Tile",
    finish: "Hand-glazed clay",
    swatch: "/products/tile-zellige.jpg",
    tag: "Artisan · Glossy",
    moods: ["Warm", "Light", "Dark"],
    colours: [
      { name: "Ivory", hex: "#eee2cd", image: "/products/tile-zellige.jpg" },
      { name: "Ochre", hex: "#c68d3e", image: "/products/tle-zellige-ochre.jpg" },
      { name: "Sage", hex: "#8a9375", image: "/products/tle-zellige-sage.jpg" },
      { name: "Indigo", hex: "#3b4763", image: "/products/tle-zellige-indigo.jpg" },
      { name: "Charcoal", hex: "#3a3a3a", image: "/products/tle-zellige-charcoal.jpg" },
    ],
  },
  {
    id: "tle-concrete-porcelain",
    name: "Concrete-look Porcelain",
    discipline: "Tile",
    finish: "Matte honed",
    swatch: "/products/tile-concrete-porcelain.jpg",
    tag: "Large-format · Matte",
    moods: ["Cool", "Neutral", "Dark"],
    colours: [
      { name: "Greige", hex: "#a8a396", image: "/products/tile-concrete-porcelain.jpg" },
      { name: "Warm Grey", hex: "#8f8b7f", image: "/products/tle-concrete-porcelain-warm-grey.jpg" },
      { name: "Cement", hex: "#8b877c", image: "/products/tle-concrete-porcelain-cement.jpg" },
      { name: "Slate", hex: "#5a5852", image: "/products/tle-concrete-porcelain-slate.jpg" },
      { name: "Ink", hex: "#2d2d2c", image: "/products/tle-concrete-porcelain-ink.jpg" },
    ],
  },
  {
    id: "tle-herringbone-subway",
    name: "Herringbone Subway",
    discipline: "Tile",
    finish: "Glossy ceramic",
    swatch: "/products/tile-herringbone-subway.jpg",
    tag: "Wet area · Classic",
    moods: ["Light", "Cool", "Warm"],
    colours: [
      { name: "Off-white", hex: "#ebe7dc", image: "/products/tile-herringbone-subway.jpg" },
      { name: "Blush", hex: "#d4a696", image: "/products/tle-herringbone-subway-blush.jpg" },
      { name: "Bone", hex: "#c8beac", image: "/products/tle-herringbone-subway-bone.jpg" },
      { name: "Sage", hex: "#93a190", image: "/products/tle-herringbone-subway-sage.jpg" },
      { name: "Charcoal", hex: "#38383a", image: "/products/tle-herringbone-subway-charcoal.jpg" },
    ],
  },
  // ── STONE — natural, honed, dramatic ──────────────────────────
  {
    id: "stn-taj-mahal-quartzite",
    name: "Taj Mahal Quartzite",
    discipline: "Stone",
    finish: "Polished · Soft wave veining",
    swatch: "/products/stone-taj-mahal.jpg",
    tag: "Statement · Cream",
    moods: ["Warm", "Light", "Natural"],
    colours: [
      { name: "Cream", hex: "#e4d6b8", image: "/products/stone-taj-mahal.jpg" },
      { name: "Cream Amber", hex: "#dcc490", image: "/products/stn-taj-mahal-quartzite-cream-amber.jpg" },
      { name: "Amber", hex: "#c99c5c", image: "/products/stn-taj-mahal-quartzite-amber.jpg" },
      { name: "Honey", hex: "#b0824a", image: "/products/stn-taj-mahal-quartzite-honey.jpg" },
      { name: "Champagne", hex: "#e7d7b2", image: "/products/stn-taj-mahal-quartzite-champagne.jpg" },
    ],
  },
  {
    id: "stn-travertine-crazy",
    name: "Travertine · Crazy Paver",
    discipline: "Stone",
    finish: "Tumbled",
    swatch: "/products/stone-travertine-crazy.jpg",
    tag: "Freeform",
    moods: ["Warm", "Natural", "Light"],
    colours: [
      { name: "Ivory", hex: "#ded1b7", image: "/products/stone-travertine-crazy.jpg" },
      { name: "Alabaster", hex: "#eae0c8", image: "/products/stn-travertine-crazy-alabaster.jpg" },
      { name: "Silver", hex: "#c2bbae", image: "/products/stn-travertine-crazy-silver.jpg" },
      { name: "Walnut", hex: "#8f6b46", image: "/products/stn-travertine-crazy-walnut.jpg" },
      { name: "Chocolate", hex: "#7a4d2b", image: "/products/stn-travertine-crazy-chocolate.jpg" },
    ],
  },
  {
    id: "stn-honed-limestone",
    name: "Honed Limestone",
    discipline: "Stone",
    finish: "Honed",
    swatch: "/products/stone-honed-limestone.jpg",
    tag: "Cross-cut · Calm",
    moods: ["Neutral", "Natural", "Warm"],
    colours: [
      { name: "Cream", hex: "#d9ccb0", image: "/products/stone-honed-limestone.jpg" },
      { name: "Pearl", hex: "#e2ddce", image: "/products/stn-honed-limestone-pearl.jpg" },
      { name: "Sand", hex: "#c9b797", image: "/products/stn-honed-limestone-sand.jpg" },
      { name: "Beige", hex: "#c1af8f", image: "/products/stn-honed-limestone-beige.jpg" },
      { name: "Grey", hex: "#a09b8f", image: "/products/stn-honed-limestone-grey.jpg" },
    ],
  },
  {
    id: "stn-marble-bookmatch",
    name: "Statuario Marble · Book-matched",
    discipline: "Stone",
    finish: "Polished · Mirror seam",
    swatch: "/products/stone-marble-bookmatch.jpg",
    tag: "Statement · White",
    moods: ["Light", "Cool", "Dark"],
    colours: [
      { name: "Statuario", hex: "#efeee7", image: "/products/stone-marble-bookmatch.jpg" },
      { name: "Calacatta", hex: "#e4dfd0", image: "/products/stn-marble-bookmatch-calacatta.jpg" },
      { name: "Arabescato", hex: "#dcd6c3", image: "/products/stn-marble-bookmatch-arabescato.jpg" },
      { name: "Emperador", hex: "#4d3020", image: "/products/stn-marble-bookmatch-emperador.jpg" },
      { name: "Nero", hex: "#252526", image: "/products/stn-marble-bookmatch-nero.jpg" },
    ],
  },
  {
    id: "stn-basaltina",
    name: "Basaltina Lava Stone",
    discipline: "Stone",
    finish: "Honed",
    swatch: "/products/stone-basaltina.jpg",
    tag: "Dark · Volcanic",
    moods: ["Dark", "Cool"],
    colours: [
      { name: "Charcoal", hex: "#4b4a48", image: "/products/stone-basaltina.jpg" },
      { name: "Iron", hex: "#5c5b58", image: "/products/stn-basaltina-iron.jpg" },
      { name: "Slate", hex: "#4a4d50", image: "/products/stn-basaltina-slate.jpg" },
      { name: "Ink", hex: "#2c2c2c", image: "/products/stn-basaltina-ink.jpg" },
      { name: "Basalt", hex: "#3a3a38", image: "/products/stn-basaltina-basalt.jpg" },
    ],
  },
  {
    id: "stn-bluestone",
    name: "Belgian Bluestone",
    discipline: "Stone",
    finish: "Honed · Slate blue-grey",
    swatch: "/products/stone-bluestone.jpg",
    tag: "Refined · Blue",
    moods: ["Cool", "Dark"],
    colours: [
      { name: "Blue-Grey", hex: "#606970", image: "/products/stone-bluestone.jpg" },
      { name: "Storm", hex: "#4a5158", image: "/products/stn-bluestone-storm.jpg" },
      { name: "Slate", hex: "#3b4046", image: "/products/stn-bluestone-slate.jpg" },
      { name: "Graphite", hex: "#333438", image: "/products/stn-bluestone-graphite.jpg" },
      { name: "Ink", hex: "#252529", image: "/products/stn-bluestone-ink.jpg" },
    ],
  },
];

export const PRODUCT_DISCIPLINES = ["All", "Timber", "Carpet", "Tile", "Stone"];

// Colour-mood families used by the second filter row on /collection.
export const PRODUCT_MOODS = ["All", "Warm", "Cool", "Neutral", "Dark", "Light", "Natural"];
